import { useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { graphql, useStaticQuery } from 'gatsby';
import { motion } from 'framer-motion'

import LanguageSwitcher from './LanguageSwitcher'
import Link from '../Atoms/Link';

import Search from '../../images/icons/search.svg'
import HamburgerOpen from '../../images/icons/hamburger-open.svg'
import HamburgerClosed from '../../images/icons/hamburger-closed.svg'
import SearchOverlay from './SearchOverlay'
import { ABOUT_US_ROUTE, BUYING_INDEX_ROUTE, CONTACT_ROUTE, SELLING_INDEX_ROUTE } from '../../utils/routes/const';

interface HeaderProps {
  bgColor: string;
  hasStickyChildren: boolean;
}

type HeaderNavigationLink = {
  name: string,
  url: string
}

const headerNavigationLinks: HeaderNavigationLink[] = [
  {
    name: 'navigation.sell',
    url: SELLING_INDEX_ROUTE,
  },
  {
    name: 'navigation.buy',
    url: BUYING_INDEX_ROUTE
  },
  {
    name: 'navigation.aboutUs',
    url: ABOUT_US_ROUTE,
  },
  {
    name: 'navigation.contact',
    url: CONTACT_ROUTE,
  },
  {
    name: 'navigation.freeEstimate',
    url: SELLING_INDEX_ROUTE,
  },
]

const imageQuery = graphql`
query {
  logo: file(base: {eq: "logo.svg"}) {
    publicURL
  }
}
`;

// See https://codesandbox.io/s/f7z9m?file=/src/App.js for the animation
const headerVariants = {
  opened: {
    y: "0%",
    transition: {
      duration: 0.9,
      ease: [0.74, 0, 0.19, 1.02]
    }
  },
  closed: {
    y: "-120%",
    transition: {
      duration: 0.5,
      ease: [0.74, 0, 0.19, 1.02]
    }
  }
}

const ulVariant = {
  opened: {
    transition: {
      delayChildren: 1,
      staggerChildren: 0.3
    }
  },
  closed: {
    transition: {
      staggerChildren: 0.1,
      staggerDirection: -1
    }
  }
};

const headerItemVariants = {
  opened: {
    opacity: 1,
    y: "0%",
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  closed: {
    opacity: 0,
    y: "100%",
    transition: {
      duration: 0.25,
      ease: "easeInOut"
    }
  }
}

const fadeInVariant = {
  opened: {
    opacity: 1,
  },
  closed: { opacity: 0 }
};

// for some reason motion is needed to make the animation work
const AnimatedLink = motion(Link);
const AnimatedLanguageSwitcher = motion(LanguageSwitcher);

const Header = ({ bgColor, hasStickyChildren }: HeaderProps) => {
  const { t } = useTranslation();
  const [isHamburgerOpen, openHamburger] = useState(false)
  const [isSearchOverlayOpen, openSearchOverlay] = useState(false)
  const { logo } = useStaticQuery(imageQuery);

  return (
    <header
      className={`mx-auto transition-all duration-300 w-full fixed px-2 py-4 sm:px-6 xl:px-8 flex justify-between items-center h-16 z-40 ${bgColor} ${hasStickyChildren ? 'top-0' : ''}`}
    >
      <nav className="hidden xl:block">
        <ul className="flex flex-row items-center">
          {headerNavigationLinks.map((item, key) => (
            <li className="mr-6 hidden xl:block" key={`${item.url}-${key}`}>
              <Link to={item.url} className="text-primary text-center font-roboto font-light">
                {t(item.name)}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <Link to="/" className="ml-8 xl:ml-0 no-animation xl:absolute xl:right-1/2 xl:translate-x-1/2">
        <img src={logo.publicURL} alt="logo" className="h-10" width={180} height={40} />
      </Link>
      <nav data-test="header-nav">
        <ul className="flex flex-row items-center">
          <li>
            <Link to="/magazine" className="mr-6 font-roboto text-primary hidden xl:block">
              {t('navigation.magazine')}
            </Link>
          </li>
          <li className="hidden xl:block">
            <LanguageSwitcher />
          </li>
          <li>
            <SearchOverlay isOpen={isSearchOverlayOpen} close={() => openSearchOverlay(!isSearchOverlayOpen)} />
            <Search width={20} className="mr-6 hidden xl:block cursor-pointer" onClick={() => openSearchOverlay(!isSearchOverlayOpen)} />
          </li>
          <li
            className="flex"
          >
            <button
              className="p-6"
              aria-label='Open hamburger menu'
              onClick={() => openHamburger(!isHamburgerOpen)}
            >
              {
                isHamburgerOpen ?
                  <HamburgerOpen width={20} className="xl:hidden" /> :
                  <HamburgerClosed width={20} className="xl:hidden" />
              }
            </button>
          </li>
        </ul>
      </nav>
      <motion.nav
        className={`absolute z-10 top-16 left-0 w-screen h-screen xl:hidden ${bgColor}`}
        variants={headerVariants}
        initial="closed"
        animate={isHamburgerOpen ? "opened" : "closed"}
        style={{ height: 'calc(100vh - 4rem)' }}
      >
        <motion.ul className="flex flex-col justify-center mt-32" variants={ulVariant}>
          {headerNavigationLinks.map((item, key) => (
            <motion.li
              key={`${item.url}-${key}`}
              whileTap={{ scale: 0.95 }}
              className="font-roboto xl:hidden mt-5 text-center"
            >
              <AnimatedLink to={item.url}
                variants={headerItemVariants}
                className="inline-block">
                {t(item.name)}
              </AnimatedLink>
            </motion.li>
          ))}
          <motion.li whileTap={{ scale: 0.95 }} className="font-roboto mt-5 text-center xl:hidden">
            <AnimatedLink to="/magazine"
              className="inline-block"
              variants={headerItemVariants}>
              {t('navigation.magazine')}
            </AnimatedLink>
          </motion.li>
          <motion.li whileTap={{ scale: 0.95 }} className="flex justify-center mt-5 xl:hidden" variants={fadeInVariant}>
            <Search width={20} onClick={() => openSearchOverlay(!isSearchOverlayOpen)} />
          </motion.li>
          <motion.li className="absolute bottom-28 right-1/2 translate-x-1/2">
            <AnimatedLanguageSwitcher className="!block !xl:hidden" variants={fadeInVariant} />
          </motion.li>
        </motion.ul>
      </motion.nav>
    </header>
  )
}

export default Header

